import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone } from "lucide-react";
import { useSeo } from "../lib/seo";
import { BOOKING_URL, PHONE_TEL, PHONE_DISPLAY } from "../components/Header";

export default function NotFound() {
  // Signal 404 to prerenderers / bots that can read a meta tag.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, follow";
    document.head.appendChild(meta);
    const statusMeta = document.createElement("meta");
    statusMeta.name = "prerender-status-code";
    statusMeta.content = "404";
    document.head.appendChild(statusMeta);
    return () => {
      meta.remove();
      statusMeta.remove();
    };
  }, []);

  useSeo({
    title: "Page Not Found — Westbridge Inn & Suites",
    description:
      "The page you were looking for doesn't exist. Head back home or call the front desk at (641) 437-7272.",
    canonical: "https://westbridgehotels.com/404",
  });

  return (
    <main id="main" className="min-h-[70vh] bg-cream pt-32 pb-24" data-testid="not-found-page">
      <div className="container-app max-w-2xl">
        <p className="eyebrow mb-4">404 · Page not found</p>
        <h1 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-forest-900 tracking-tight">
          That page doesn&rsquo;t exist. But the front desk is still open.
        </h1>
        <p className="mt-6 text-ink/80 text-lg leading-relaxed">
          You may have followed an outdated link. Here are the places most guests are actually looking for:
        </p>
        <ul className="mt-8 grid sm:grid-cols-2 gap-3">
          {[
            ["Rooms & Suites", "/rooms"],
            ["Amenities & Indoor Pool", "/amenities"],
            ["Local Guides (Explore)", "/explore"],
            ["Stay With Us (Audience Pages)", "/stay"],
            ["Journal", "/blog"],
            ["FAQ (100+ answers)", "/faq"],
            ["About", "/about"],
            ["Contact & Directions", "/contact"],
          ].map(([label, href]) => (
            <li key={href}>
              <Link
                to={href}
                className="block px-5 py-3 border border-tan bg-cream-50 hover:border-brass hover:bg-cream-100 text-forest-900 transition-colors"
                data-testid={`not-found-link-${href.replace(/\//g, "-")}`}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/" className="btn-primary" data-testid="not-found-home-cta">
            <ArrowLeft size={14} /> Go home
          </Link>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-dark"
            data-testid="not-found-book-cta"
          >
            Book Now <ArrowUpRight size={14} />
          </a>
          <a href={`tel:${PHONE_TEL}`} className="btn-ghost-dark" data-testid="not-found-call-cta">
            <Phone size={14} /> Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </main>
  );
}
